// ABOUTME: Sends approved WhatsApp templates to a selected conversation.
// ABOUTME: Resolves job context and segment-aware request identity for template sends.
import React from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { apiDirectCall, useApiCache } from "customHooks/useApi";
import Loading from "customHooks/useCompleoForm/helpers/Loading";
import { getWhatsappApiResponseData } from "functions/whatsappApiResponse";
import {
    buildWhatsappContactEpisodeActionRequest,
    createWhatsappActionRequestTracker,
    WhatsappContactEpisodeActionContext
} from "functions/whatsappContactEpisode";
import {
    analyseWhatsappTemplate,
    getApiErrorMessage,
    getWhatsappTemplateName,
    getWhatsappTemplateSendContext
} from "Pages/Applicant/ApplicantView/ApplicantWhatsapp/helpers";
import { useTranslation } from "react-i18next";

const moduleName = "WHATSAPPINBOX";

const normalizeDialogLanguage = (language?: string) =>
    String(language || "")
        .toLowerCase()
        .startsWith("pt")
        ? "pt-BR"
        : "en-US";

interface WhatsappTemplateJobOption {
    jobId: string;
    label: string;
}

interface WhatsappTemplateSendDialogProps {
    open: boolean;
    companyId: number;
    applicantId: string;
    segmentId?: string;
    conversationScope?: "APPLICANT_TIMELINE";
    language?: string;
    defaultJobId?: string;
    jobOptions?: WhatsappTemplateJobOption[];
    contactEpisodeActionContext?: WhatsappContactEpisodeActionContext;
    onClose: () => void;
    onSent: () => void | Promise<void>;
    showMessage: (message: string, variant?: any) => void;
}

interface WhatsappTemplateSendRequest {
    companyId: number;
    applicantId: string;
    segmentId?: string;
    conversationScope?: "APPLICANT_TIMELINE";
    templateName: string;
    languageCode: string;
    jobId?: string;
}

export const buildWhatsappTemplateSendRequest = (
    request: WhatsappTemplateSendRequest,
    contactEpisodeActionContext?: WhatsappContactEpisodeActionContext,
    actionRequestId?: string
) =>
    buildWhatsappContactEpisodeActionRequest(
        {
            ...request,
            segmentId: request.segmentId || undefined,
            jobId: request.jobId || undefined
        },
        contactEpisodeActionContext,
        actionRequestId
    );

export const normalizeWhatsappTemplateJobId = (value?: string) =>
    String(value || "")
        .trim()
        .replace(/^JOB:/i, "");

export const getInitialTemplateJobId = (
    defaultJobId?: string,
    jobOptions: WhatsappTemplateJobOption[] = []
) => {
    const normalizedDefaultJobId = normalizeWhatsappTemplateJobId(defaultJobId);
    if (normalizedDefaultJobId) {
        return normalizedDefaultJobId;
    }

    return jobOptions.length === 1 ? jobOptions[0].jobId : "";
};

export const resolveTemplateSelectedJobId = (
    currentJobId: string,
    defaultJobId?: string,
    jobOptions: WhatsappTemplateJobOption[] = []
) => {
    const normalizedCurrentJobId = normalizeWhatsappTemplateJobId(currentJobId);
    if (
        normalizedCurrentJobId &&
        jobOptions.some((job) => job.jobId === normalizedCurrentJobId)
    ) {
        return normalizedCurrentJobId;
    }

    return getInitialTemplateJobId(defaultJobId, jobOptions);
};

export const resolveWhatsappTemplateSendErrorMessage = (
    error: any,
    t: (key: string) => string,
    fallbackMessage: string
) => {
    const translationCode = error?.response?.data?.translationCode;

    if (typeof translationCode === "string" && translationCode.trim()) {
        const translated = t(translationCode);

        if (translated && translated !== translationCode) {
            return translated;
        }
    }

    return getApiErrorMessage(error, fallbackMessage);
};

const WhatsappTemplateSendDialog = (props: WhatsappTemplateSendDialogProps) => {
    const {
        open,
        companyId,
        applicantId,
        segmentId,
        conversationScope,
        defaultJobId,
        contactEpisodeActionContext,
        language: languageProp,
        jobOptions = [],
        onClose,
        onSent,
        showMessage
    } = props;
    const [, i18n, readyTranslation] = useTranslation(moduleName, {
        useSuspense: false
    });
    const language = normalizeDialogLanguage(
        languageProp || i18n.languages?.[0] || i18n.language
    );
    const [resourceVersion, setResourceVersion] = React.useState(0);
    const readyLanguageTranslation =
        readyTranslation && i18n.hasResourceBundle(language, moduleName);
    const t = React.useMemo(() => i18n.getFixedT(language, moduleName), [
        i18n,
        language,
        resourceVersion
    ]);
    const [selectedTemplateName, setSelectedTemplateName] = React.useState("");
    const [selectedJobId, setSelectedJobId] = React.useState(
        getInitialTemplateJobId(defaultJobId, jobOptions)
    );
    const [sending, setSending] = React.useState(false);
    const actionRequestTrackerRef = React.useRef(
        createWhatsappActionRequestTracker()
    );
    const sendLifetimeRef = React.useRef({
        generation: 0,
        scope: ""
    });
    const sendScope = `${companyId}:${applicantId}`;
    const activeDialogScopeRef = React.useRef("");
    activeDialogScopeRef.current = open ? sendScope : "";

    const [templatesState, fetchTemplates, , isTemplatesFetching] = useApiCache(
        "/whatsapp/templates",
        "post",
        {
            companyId
        },
        false,
        1000 * 60 * 5
    );
    const fetchTemplatesRef = React.useRef(fetchTemplates);

    React.useEffect(() => {
        fetchTemplatesRef.current = fetchTemplates;
    }, [fetchTemplates]);

    const normalizedJobOptions = React.useMemo(() => {
        const usedJobIds = new Set<string>();

        return jobOptions
            .map((job) => ({
                jobId: normalizeWhatsappTemplateJobId(job.jobId),
                label: job.label
            }))
            .filter((job) => {
                if (!job.jobId || usedJobIds.has(job.jobId)) {
                    return false;
                }

                usedJobIds.add(job.jobId);
                return true;
            });
    }, [jobOptions]);

    React.useEffect(() => {
        if (!open || readyLanguageTranslation) {
            return;
        }

        let disposed = false;
        i18n.loadLanguages(language, () => {
            if (!disposed) {
                setResourceVersion((current) => current + 1);
            }
        });

        return () => {
            disposed = true;
        };
    }, [i18n, language, open, readyLanguageTranslation]);

    React.useEffect(() => {
        if (open) {
            fetchTemplatesRef.current({
                companyId
            });
        }
    }, [companyId, open]);

    React.useEffect(() => {
        if (open) {
            setSelectedJobId((currentJobId) =>
                resolveTemplateSelectedJobId(
                    currentJobId,
                    defaultJobId,
                    normalizedJobOptions
                )
            );
        }
    }, [defaultJobId, normalizedJobOptions, open]);

    React.useEffect(() => {
        sendLifetimeRef.current = {
            generation: sendLifetimeRef.current.generation + 1,
            scope: open ? sendScope : ""
        };
        setSelectedTemplateName("");
        setSending(false);
        actionRequestTrackerRef.current.clear("SEND_TEMPLATE");
    }, [open, sendScope]);

    const approvedTemplates = (
        getWhatsappApiResponseData(templatesState.response).templates || []
    )
        .filter((item: Compleo.IObject) => item.status === "APPROVED")
        .sort((left: Compleo.IObject, right: Compleo.IObject) =>
            getWhatsappTemplateName(left).localeCompare(
                getWhatsappTemplateName(right)
            )
        );
    const selectedTemplate =
        approvedTemplates.find(
            (template: Compleo.IObject) =>
                getWhatsappTemplateName(template) === selectedTemplateName
        ) || null;
    const templateAnalysis = analyseWhatsappTemplate(selectedTemplate);
    const templateContext = getWhatsappTemplateSendContext(selectedTemplate);
    const requiresJob = templateContext.requiresJob;
    const showJobSelector = requiresJob && normalizedJobOptions.length > 1;
    const hasRequiredJob = !requiresJob || !!selectedJobId;
    const hasNoJobContext = requiresJob && normalizedJobOptions.length === 0;

    const handleSend = async () => {
        if (!selectedTemplate) {
            showMessage(t("templateDialogRequiredTemplate"), "error");
            return;
        }
        if (!templateAnalysis.supported) {
            showMessage(t("templateDialogUnsupportedTemplate"), "error");
            return;
        }
        if (!hasRequiredJob) {
            showMessage(t("templateDialogRequiredJob"), "error");
            return;
        }

        const templateName = getWhatsappTemplateName(selectedTemplate);
        const sendLifetime = {
            generation: sendLifetimeRef.current.generation + 1,
            scope: sendScope
        };
        sendLifetimeRef.current = sendLifetime;
        const isCurrentSend = () =>
            sendLifetimeRef.current.generation === sendLifetime.generation &&
            sendLifetimeRef.current.scope === sendLifetime.scope &&
            activeDialogScopeRef.current === sendLifetime.scope;
        const attempt = actionRequestTrackerRef.current.prepare(
            "SEND_TEMPLATE",
            JSON.stringify({
                companyId,
                applicantId,
                segmentId: segmentId || "",
                conversationScope: conversationScope || "",
                templateName,
                languageCode: selectedTemplate.language || "pt_BR",
                jobId: selectedJobId,
                contactEpisodeId:
                    contactEpisodeActionContext?.contactEpisodeId || "",
                resolutionRevision:
                    contactEpisodeActionContext?.resolutionRevision || 0
            })
        );
        try {
            setSending(true);
            await apiDirectCall(
                "/whatsapp/sendtemplate",
                "post",
                buildWhatsappTemplateSendRequest(
                    {
                        companyId,
                        applicantId,
                        segmentId,
                        conversationScope,
                        templateName,
                        languageCode: selectedTemplate.language || "pt_BR",
                        jobId: selectedJobId
                    },
                    contactEpisodeActionContext,
                    attempt.actionRequestId
                )
            );
            if (!isCurrentSend()) {
                return;
            }
            actionRequestTrackerRef.current.complete(
                "SEND_TEMPLATE",
                attempt.signature
            );
            showMessage(t("templateDialogSent"), "success");
            onClose();
            try {
                await onSent();
            } catch {
                if (isCurrentSend()) {
                    showMessage(t("loadConversationsError"), "error");
                }
            }
        } catch (error) {
            if (isCurrentSend()) {
                showMessage(
                    resolveWhatsappTemplateSendErrorMessage(
                        error,
                        t,
                        t("templateDialogLoadError")
                    ),
                    "error"
                );
            }
        } finally {
            if (isCurrentSend()) {
                setSending(false);
            }
        }
    };

    if (!readyLanguageTranslation) {
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogContent>
                    <Loading />
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t("templateDialogTitle")}</DialogTitle>
            <DialogContent>
                <TextField
                    select
                    fullWidth
                    margin="normal"
                    label={t("templateDialogTemplateField")}
                    value={selectedTemplateName}
                    onChange={(event) =>
                        setSelectedTemplateName(event.target.value)
                    }
                >
                    {approvedTemplates.map((template: Compleo.IObject) => (
                        <MenuItem
                            key={getWhatsappTemplateName(template)}
                            value={getWhatsappTemplateName(template)}
                        >
                            {`${getWhatsappTemplateName(template)}${
                                template.category
                                    ? ` (${t(
                                          "templateDialogTemplateCategory"
                                      )}: ${template.category})`
                                    : ""
                            }`}
                        </MenuItem>
                    ))}
                </TextField>

                {isTemplatesFetching && <Loading />}

                {selectedTemplate && (
                    <Box mt={1}>
                        <Alert severity="info">
                            {t("templateDialogAutomaticParameters")}
                        </Alert>
                    </Box>
                )}

                {selectedTemplate && !templateAnalysis.supported && (
                    <Box mt={1}>
                        <Alert severity="warning">
                            {t("templateDialogUnsupportedTemplate")}
                        </Alert>
                    </Box>
                )}

                {hasNoJobContext && (
                    <Box mt={1}>
                        <Alert severity="warning">
                            {t("templateDialogNoJob")}
                        </Alert>
                    </Box>
                )}

                {showJobSelector && (
                    <TextField
                        select
                        fullWidth
                        margin="normal"
                        label={t("templateDialogJobField")}
                        value={selectedJobId}
                        onChange={(event) =>
                            setSelectedJobId(
                                normalizeWhatsappTemplateJobId(
                                    String(event.target.value)
                                )
                            )
                        }
                    >
                        {normalizedJobOptions.map((job) => (
                            <MenuItem key={job.jobId} value={job.jobId}>
                                {job.label}
                            </MenuItem>
                        ))}
                    </TextField>
                )}

                {selectedTemplate && templateContext.label && (
                    <Box mt={1}>
                        <Typography variant="caption" color="textSecondary">
                            {templateContext.label}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t("close")}</Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSend}
                    disabled={
                        sending ||
                        !selectedTemplate ||
                        !templateAnalysis.supported ||
                        !hasRequiredJob
                    }
                >
                    {t("send")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WhatsappTemplateSendDialog;
